
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePaymentService } from "@/services/paymentService";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface PricingCardProps {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  originalYearlyPrice: number;
  description: string;
  features: string[];
  isPopular?: boolean;
  isAnnual: boolean;
}

export const PricingCard = ({
  name,
  monthlyPrice,
  yearlyPrice,
  originalYearlyPrice,
  description,
  features,
  isPopular,
  isAnnual,
}: PricingCardProps) => {
  const navigate = useNavigate();
  const { initiateFlutterwavePayment } = usePaymentService();
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async () => {
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to subscribe to a plan",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      setIsProcessing(true);
      
      // Calculate redirect URL (current origin + /payment-callback)
      const redirectUrl = `${window.location.origin}/payment-callback`;
      
      console.log("Initiating payment for:", name, isAnnual ? "annual" : "monthly");
      
      // Initiate payment
      const result = await initiateFlutterwavePayment(
        { name, monthlyPrice, yearlyPrice, originalYearlyPrice, description, features },
        isAnnual,
        redirectUrl
      );
      
      if (result?.paymentLink) {
        // Save transaction reference in localStorage for verification on return
        localStorage.setItem('flw_tx_ref', result.transactionRef);
        
        // Redirect to Flutterwave checkout
        window.location.href = result.paymentLink;
      } else {
        toast({
          title: "Payment Error",
          description: "Failed to initiate payment process. Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Subscribe error:", error);
      toast({
        title: "Subscription Error",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card
      className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg ${
        isPopular ? 'border-primary shadow-md scale-105' : ''
      }`}
    >
      {isPopular && (
        <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 rounded-bl-lg text-sm font-medium">
          Popular
        </div>
      )}
      <CardHeader className="pb-0">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <div className="h-4 w-4 rounded-full bg-primary" />
          </div>
          <h3 className="text-xl font-semibold">{name}</h3>
        </div>
        <div className="mb-4">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold">
              ${isAnnual ? yearlyPrice : monthlyPrice}
            </span>
            <span className="text-muted-foreground">
              /{isAnnual ? 'year' : 'month'}
            </span>
          </div>
          {isAnnual && (
            <div className="text-sm text-muted-foreground mt-1">
              <span className="line-through">${originalYearlyPrice}</span>
              <span className="text-primary ml-2">Save ${(originalYearlyPrice - yearlyPrice).toFixed(2)}</span>
            </div>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          {description}
        </p>
      </CardHeader>
      <CardContent>
        <Button 
          className={`w-full mb-6 ${
            isPopular ? 'bg-primary hover:bg-primary/90' : ''
          }`}
          onClick={handleSubscribe}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' : 'Subscribe'}
        </Button>
        <ul className="space-y-4">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <span className="text-sm text-muted-foreground">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
